import React, { useEffect, useState } from "react";
import CustomModal from "../../../components/generic/custommodal/CustomModal";
import AddEditPatientFormComponent from "./AddEditPatientFormComponent";
import SecondaryButton from "../../../components/generic/buttons/SecondaryButton";
import PrimaryButton from "../../../components/generic/buttons/PrimaryButton";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import { executeIntent } from "../../utils/IntentExecutor";
import IntentType from "../../../intentexecutor/utils/IntentType";
import { notification } from "antd";
import { useGetPatientDetailsByIdentifier } from "../../generic/hooks/useGetPatientDetailsByIdentifier";
import { Subject } from "rxjs";
import { EventType } from "../../../service/network/utils/EventType";
import { useReadMemory } from "../../generic/hooks/useReadMemory";
import MemoryType from "../../../service/memory/enum/MemoryType";
import apiService from "../../../service/apiservice/apiService";
import { strings } from "../../utils/strings";
import { statusCode } from "../../utils/statuscodes";
import { convertDateToEODTimestamp } from "../../../utils/dateutils";
import { useRecoilValue } from "recoil";
import { officeLabel } from "../../../components/recoil/officeLabelRecoil";

export const patientDataUpdateResponse = new Subject();

const AddEditPatientModal = ({
  openAddPatientModal,
  setOpenAddPatientModal,
  editPatient,
  userIdentifier,
}) => {
  const organizationIdentifier = useGetOrgIdentifier();
  const [patientDetails, patientDetailsLoading] =
    useGetPatientDetailsByIdentifier(userIdentifier);
  const userDetails = useReadMemory(MemoryType.USER);

  const [patient, setPatient] = useState({
    patientStatus: "Alive",
  });
  const [metadata, setMetadata] = useState({});
  const [diagnosis, setDiagnosis] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [careGiver, setCareGiver] = useState({});
  const label = useRecoilValue(officeLabel);

  const toggleAddPatientModal = () => {
    setOpenAddPatientModal(!openAddPatientModal);
  };

  const trimIfString = (value) =>
    typeof value === "string" ? value.trim() : value;

  const validate = (values) => {
    const errors = {};
    if (!trimIfString(values?.givenName)) {
      errors.givenName = strings.FIRST_NAME_ERROR_MESSAGE;
    }
    if (!trimIfString(values?.familyName)) {
      errors.familyName = strings.LAST_NAME_ERROR_MESSAGE;
    }
    if (!trimIfString(values?.medicalRecordNumber)) {
      errors.medicalRecordNumber = strings.MRN_ERROR_MESSAGE;
    }
    if (!values?.serviceLocationType) {
      errors.serviceLocationType = strings.LOCATION_ERROR_MESSAGE;
    }
    if (
      values?.serviceLocationType === "home" &&
      !trimIfString(values?.completeAddress)
    ) {
      errors.completeAddress = strings.ADDRESS_ERROR_MESSAGE;
    }
    if (values?.serviceLocationType === "facility" && !values?.facilityId) {
      errors.facility = strings.FACILITY_ERROR_MESSAGE;
    }
    if (!values?.teams?.length) {
      errors.teams = `${label} is required!`;
    }
    return errors;
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setFormErrors(validate(patient));
    setIsSubmit(true);
  };

  const handleResponse = (response, successMessage, errorMessage) => {
    if (response.identifier) {
      notification.success({
        message: successMessage,
        placement: "bottomRight",
      });
      return true;
    } else {
      notification.error({
        message: errorMessage,
        placement: "bottomRight",
      });
      return false;
    }
  };

  const handleError = () => {
    notification.error({
      message: "Something went wrong!",
      placement: "bottomRight",
    });
  };

  const commonPayloadData = () => ({
    medicalRecordNumber: trimIfString(patient?.medicalRecordNumber),
    gender: patient?.gender,
    birthDate:
      patient?.birthDate && convertDateToEODTimestamp(patient?.birthDate),
    admitDate:
      patient?.admitDate && convertDateToEODTimestamp(patient?.admitDate),
    certificateEndDate:
      patient?.certificateEndDate &&
      convertDateToEODTimestamp(patient?.certificateEndDate),
    pronoun: trimIfString(patient?.pronoun),
    givenName: trimIfString(patient?.givenName),
    familyName: trimIfString(patient?.familyName),
    email: trimIfString(patient?.email),
    telephone: trimIfString(patient?.telephone),
    serviceLocationType: patient?.serviceLocationType,
    levelOfCare: patient?.levelOfCare,
    organizationIdentifier: organizationIdentifier,
    latitude: trimIfString(patient?.latitude),
    longitude: trimIfString(patient?.longitude),
    teams: patient?.teams,
    comments: trimIfString(patient?.comments),
    ...(patient?.serviceLocationType === "facility" && {
      facilityId: trimIfString(patient?.facilityId),
    }),
    address: {
      streetAddress: trimIfString(patient?.streetAddress),
      addressLocality: trimIfString(patient?.addressLocality),
      addressRegion: trimIfString(patient?.addressRegion),
      postalCode: trimIfString(patient?.postalCode),
      completeAddress: trimIfString(patient?.completeAddress),
      addressCountry: trimIfString(patient?.addressCountry),
    },
    patientStatus: patient?.patientStatus,
    teamName: trimIfString(patient?.teamName),
    metadata: JSON.stringify({ ...metadata, careGiver }),
    creatorIdentifier: userDetails?.userIdentifier,
    isActive: patient?.patientStatus === "Alive" ? true : false,
    diagnosis: JSON.stringify(diagnosis || []),
  });

  const errorMessages = {
    [statusCode.EMAIL_ALREADY_EXISTS]: {
      email: strings.EMAIL_ALREADY_EXISTS,
    },
    [statusCode.TELEPHONE_ALREADY_EXISTS]: {
      telephone: strings.TELEPHONE_ALREADY_EXISTS,
    },
    [statusCode.MRN_ALREADY_EXISTS]: {
      medicalRecordNumber: strings.MRN_ALREADY_EXISTS,
    },
  };

  const addPatient = async () => {
    setUpdateLoading(true);
    let response;
    const payload = { data: commonPayloadData() };

    try {
      response = await executeIntent(IntentType.ADD_PATIENT, payload);
      if (errorMessages[response?.code]) {
        setFormErrors(errorMessages[response.code]);
      } else {
        handleResponse(
          response,
          "Patient added successfully!",
          "Could not add patient!"
        );
        patientDataUpdateResponse.next({
          type: EventType.PATIENT_DATA_UPDATED,
        });
      }
    } catch (err) {
      handleError();
    } finally {
      setUpdateLoading(false);
      if (
        response?.code !== statusCode.EMAIL_ALREADY_EXISTS &&
        response?.code !== statusCode.TELEPHONE_ALREADY_EXISTS &&
        response?.code !== statusCode.MRN_ALREADY_EXISTS
      ) {
        toggleAddPatientModal();
      }
    }
  };

  const updatePatient = async () => {
    let response;
    const endpoint =
      "niramayafoundation/niramayavisits/api/v1/careplan/markcomplete";
    const params = {
      patientIdentifier: patient.userIdentifier,
    };
    setUpdateLoading(true);
    const payload = {
      data: {
        ...commonPayloadData(),
        userIdentifier: userIdentifier,
        birthDate:
          patient?.birthDate && convertDateToEODTimestamp(patient?.birthDate),
        admitDate:
          patient?.admitDate && convertDateToEODTimestamp(patient?.admitDate),
        certificateEndDate:
          patient.certificateEndDate &&
          convertDateToEODTimestamp(patient?.certificateEndDate),
      },
    };
    try {
      response = await executeIntent(IntentType.UPDATE_PATIENT, payload);
      if (response.isActive === false) {
        await apiService.get(endpoint, params);
      }
      if (errorMessages[response?.code]) {
        setFormErrors(errorMessages[response.code]);
      } else {
        handleResponse(
          response,
          "Patient details updated successfully",
          "Could not update patient details"
        );
        patientDataUpdateResponse.next({
          type: EventType.PATIENT_DATA_UPDATED,
        });
      }
    } catch (err) {
      handleError();
    } finally {
      setUpdateLoading(false);
      if (
        response?.code !== statusCode.EMAIL_ALREADY_EXISTS &&
        response?.code !== statusCode.TELEPHONE_ALREADY_EXISTS &&
        response?.code !== statusCode.MRN_ALREADY_EXISTS
      ) {
        toggleAddPatientModal();
      }
    }
  };

  useEffect(() => {
    const parsedMetadata = patientDetails?.metadata
      ? JSON.parse(patientDetails?.metadata)
      : "";
    const parseDiagnosis = patientDetails?.diagnosis
      ? JSON.parse(patientDetails?.diagnosis)
      : [];
    setPatient({...patient,...patientDetails});
    setMetadata(parsedMetadata);
    setCareGiver(parsedMetadata.careGiver);
    setDiagnosis(parseDiagnosis);
  }, [patientDetails]);

  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmit) {
      if (editPatient) {
        updatePatient();
      } else {
        addPatient();
      }
    }
  }, [formErrors]);

  const footerButtonsForAddVolunteer = (
    <>
      <SecondaryButton
        buttonText={"Cancel"}
        handleOnClick={toggleAddPatientModal}
      />
      <PrimaryButton
        buttonText={editPatient ? "Save" : "Add"}
        handleOnClick={handleSubmitForm}
        loading={updateLoading}
      />
    </>
  );

  return (
    <>
      <CustomModal
        closable={false}
        isModalOpen={openAddPatientModal}
        footer={footerButtonsForAddVolunteer}
        closeModal={setOpenAddPatientModal}
        title={editPatient ? "Edit Patient" : "Add Patient"}
      >
        <AddEditPatientFormComponent
          patient={patient}
          setPatient={setPatient}
          metadata={metadata}
          setMetadata={setMetadata}
          formErrors={formErrors}
          patientDetailsLoading={patientDetailsLoading}
          setDiagnosis={setDiagnosis}
          diagnosis={diagnosis}
          careGiver={careGiver}
          setCareGiver={setCareGiver}
          label={label}
        />
      </CustomModal>
    </>
  );
};

export default AddEditPatientModal;
